/**
 * THE CASE FILE (moved here by P39)
 *
 * The internal record of a request: every mail, every attachment, every note a
 * colleague wrote. It was the first of two tabs on `/tickets/:n`; the second
 * was the audit trail, which P39 moved into the right rail (`TicketDetailsRail`)
 * because a ledger is what you read WHILE looking at something else. What is
 * left needed no tab — adding to the case file is an action, so it is a card in
 * the work column with its own heading.
 *
 * **The sentence about what a note is stays** (P37b, task 5). Katia pointed at
 * this tab and asked whether she could answer a customer from it:
 * `tickets-notes-create` writes `Dra.CaseFileEntries`, which never leaves the
 * house, and nothing on the screen said so. It says so beside the box, and it
 * names the control that does reach the customer.
 *
 * **One card, and the form opens.** Until P39 the note form was a card of its
 * own, permanently open above the case file — 338 px of empty boxes on a screen
 * whose whole finding was that it is too long, for an action most readings of a
 * ticket never take. It is the same form, in the card it writes to, behind the
 * button that names it; the sentence about what a note IS moved with it, so it
 * is read at the moment somebody starts typing rather than skimmed over.
 *
 * What P39 also changed: the title input measured **20 px high** and the
 * textarea carried the same hand-rolled `border-gray-300`; both are the kit's
 * now, so they are 36 px with the one focus ring (§4 — never under 32 px).
 */
import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Field, Input, Textarea } from './ui/Field';
import { isoDateTime } from '../lib/dates';
import type { CaseFileEntry } from '../types/domain';

export function CaseFileCard({
    entries, canEdit, noteTitle, noteDetail, pending, onTitleChange, onDetailChange, onAdd,
}: {
    entries: CaseFileEntry[];
    canEdit: boolean;
    noteTitle: string;
    noteDetail: string;
    pending: boolean;
    onTitleChange: (v: string) => void;
    onDetailChange: (v: string) => void;
    onAdd: () => void;
}) {
    const [writing, setWriting] = useState(false);
    const formRef = useRef<HTMLDivElement>(null);

    // The form is useless without the keyboard in it, and a reader who pressed
    // the button has already said what they want to do.
    useEffect(() => {
        if (writing) formRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    }, [writing]);

    return (
        <Card
            title="Case file"
            subtitle={`${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} — every mail, attachment and note on this request`}
            action={canEdit && !writing
                ? <Button variant="secondary" size="sm" onClick={() => setWriting(true)}>Add a note</Button>
                : undefined}
        >
            {canEdit && writing && (
                <div ref={formRef} className="mb-4 rounded-card border border-hair-2 bg-ground p-3">
                    <p className="text-xs text-muted">
                        Internal only — a note is for colleagues and is never sent to anybody outside the house.
                        To answer the customer, use the <span className="font-medium">Reply</span> card above.
                    </p>
                    <div className="mt-3 space-y-3">
                        <Field label="Title">
                            {p => (
                                <Input
                                    {...p}
                                    value={noteTitle}
                                    onChange={e => onTitleChange(e.target.value)}
                                    placeholder="What happened, in one line"
                                />
                            )}
                        </Field>
                        <Field label="Detail" hint="Optional.">
                            {p => <Textarea {...p} rows={2} value={noteDetail} onChange={e => onDetailChange(e.target.value)} />}
                        </Field>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={!noteTitle.trim() || pending}
                            disabledReason="A note needs a title — it is what the case file lists."
                            onClick={onAdd}
                        >
                            Add note
                        </Button>
                        <Button variant="quiet" size="sm" onClick={() => setWriting(false)}>Cancel</Button>
                    </div>
                </div>
            )}

            {entries.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted">No case file entries yet.</p>
            ) : (
                <ul className="space-y-3">
                    {/* The index is in the key because the same mail can legitimately be
                        in the case file twice: `DRA-2026-00053` carries communication 91
                        as its Origin AND as a FollowUp, which is P27's shape, not a bug —
                        and the kind/ref/time triple is identical for both. React logged
                        "two children with the same key" on every load of such a ticket. */}
                    {entries.map((e, i) => (
                        <li key={`${e.entryKind}-${e.refId}-${e.occurredAt}-${i}`}>
                            <CaseFileRow entry={e} />
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

/**
 * A mail, with its height bounded (P39).
 *
 * The case file rendered every body in full, which makes the page as long as the
 * longest mail in the thread — and the mails in this mailbox are routinely a
 * colleague's forward of a whole chain. Measured on `DRA-2026-00053`, whose two
 * entries are the SAME mail (P27's Origin + FollowUp shape), the two bodies
 * alone were about 460 px of a 2'000 px page.
 *
 * A short mail is never touched: the cap is only applied once the rendered body
 * exceeds it, which is measured after paint rather than guessed from the
 * markup — the body is customer HTML and its height is not a function of
 * anything this component can see. What is cut is always recoverable in one
 * click, and the request itself is short enough to sit above the fold.
 */
const MAIL_BODY_CAP_PX = 220;

function MailBody({ html }: { html: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [tall, setTall] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (el) setTall(el.scrollHeight > MAIL_BODY_CAP_PX + 24);
    }, [html]);

    return (
        <div className="mt-2">
            <div
                ref={ref}
                style={tall && !open ? { maxHeight: MAIL_BODY_CAP_PX, overflow: 'hidden' } : undefined}
                className="max-w-none rounded-ctl border border-hair-2 bg-ground p-2 text-sm leading-relaxed text-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
            />
            {tall && (
                <Button variant="quiet" size="sm" className="-ml-2.5" onClick={() => setOpen(!open)}>
                    {open ? 'Show less of this mail' : 'Show the whole mail'}
                </Button>
            )}
        </div>
    );
}

export function CaseFileRow({ entry }: { entry: CaseFileEntry }) {
    return (
        <div className="rounded-card border border-hair-2 bg-surface p-3 text-sm">
            <div className="flex justify-between gap-2 text-xs text-muted">
                <span>{entry.entryKind}{entry.subKind ? ` · ${entry.subKind}` : ''}</span>
                <span className="shrink-0 tabular-nums">{isoDateTime(entry.occurredAt)}</span>
            </div>
            <div className="mt-1 font-medium text-ink">{entry.title}</div>
            {entry.communication && (
                <div className="mt-2 border-t border-hair-2 pt-2 text-xs text-muted">
                    <div className="flex flex-wrap items-center gap-2">
                        <span>
                            {entry.communication.direction === 'Inbound' ? 'From' : 'To'}: {entry.communication.fromAddress ?? entry.communication.toAddresses}
                        </span>
                        {/* Neutral, and therefore no glyph (§8): "this mail reached us
                            through a colleague" is a fact about the mail, not a question
                            anybody has to answer. It used to be an amber pill, which said
                            the opposite. */}
                        {entry.communication.isRelayed && entry.communication.originalSenderAddress && (
                            <Badge tone="neutral" title="A colleague forwarded this mail. The customer named in the ticket is the ORIGINAL sender, not the forwarder.">
                                relayed from {entry.communication.originalSenderAddress}
                            </Badge>
                        )}
                    </div>
                    {entry.communication.bodyHtml && <MailBody html={entry.communication.bodyHtml} />}
                </div>
            )}
        </div>
    );
}
